import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import localforage from 'localforage';
import * as XLSX from 'xlsx';
import {
  Upload, Users, Home, Printer, CheckCircle, AlertCircle, RefreshCw,
  X, UserCheck, UserX, HelpCircle, Info, IdCard, Search, Pencil, Check
} from 'lucide-react';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

// ── Name matching helpers ────────────────────────────────────────────────
//
// The live account data has three separate quirks that all show up on real
// Welcome Days rosters:
//  1. Usernames are usually "Vorname Nachname" (space), but a few legacy
//     accounts use "vorname.nachname" (dot) — both are tried.
//  2. Some accounts only ever got a first name, no last name at all
//     ("Bijoux", "Maria") — those match on first name alone.
//  3. Special characters got mangled server-side at some point: an account
//     stored as "Ela Ã–zkul" is what you get when the UTF-8 bytes for
//     "Ela Özkul" get misread as Windows-1252. We reverse that specific
//     corruption, and separately also try the "ö"->"oe" transliteration
//     some accounts use instead of a bare diacritic drop.
// On top of that, names are compared token-by-token (not as one glued
// string) so a stored middle name ("Tatiana Monteiro Fernandes") doesn't
// break a match against a two-word roster entry ("Tatiana" / "Fernandes").

const CP1252_HIGH_TO_CODEPOINT = {
  0x80: 0x20ac, 0x82: 0x201a, 0x83: 0x0192, 0x84: 0x201e, 0x85: 0x2026, 0x86: 0x2020, 0x87: 0x2021,
  0x88: 0x02c6, 0x89: 0x2030, 0x8a: 0x0160, 0x8b: 0x2039, 0x8c: 0x0152, 0x8e: 0x017d,
  0x91: 0x2018, 0x92: 0x2019, 0x93: 0x201c, 0x94: 0x201d, 0x95: 0x2022, 0x96: 0x2013, 0x97: 0x2014,
  0x98: 0x02dc, 0x99: 0x2122, 0x9a: 0x0161, 0x9b: 0x203a, 0x9c: 0x0153, 0x9e: 0x017e, 0x9f: 0x0178,
};
const CODEPOINT_TO_CP1252_BYTE = Object.fromEntries(
  Object.entries(CP1252_HIGH_TO_CODEPOINT).map(([byte, cp]) => [cp, Number(byte)])
);

// Reverses "UTF-8 bytes misread as Windows-1252" corruption, e.g. "Ã–" -> "Ö".
// Returns null if the string isn't valid re-encoded UTF-8 (i.e. wasn't corrupted this way).
const fixMojibake = (str) => {
  const bytes = [];
  for (const ch of str) {
    const cp = ch.codePointAt(0);
    if (CODEPOINT_TO_CP1252_BYTE[cp] !== undefined) bytes.push(CODEPOINT_TO_CP1252_BYTE[cp]);
    else if (cp <= 0xff) bytes.push(cp);
    else return null;
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(bytes));
  } catch {
    return null;
  }
};

const stripDiacritics = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z]/g, '');

// All plausible normalized forms of a single word (not a whole name).
const wordVariants = (s) => {
  const original = (s || '').toString().trim();
  if (!original) return new Set();
  const fixed = fixMojibake(original);
  const forms = [original, ...(fixed && fixed !== original ? [fixed] : [])];
  const variants = new Set();
  for (const form of forms) {
    const raw = form.toLowerCase();
    const translit = raw.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
    variants.add(stripDiacritics(raw));
    variants.add(stripDiacritics(translit));
  }
  return variants;
};

const keysOverlap = (a, b) => {
  for (const k of a) if (k && b.has(k)) return true;
  return false;
};

// A name string -> one variant-set per word ("." or whitespace separated).
const tokenize = (s) => {
  const raw = (s || '').toString().trim();
  if (!raw) return [];
  const parts = raw.includes('.') ? raw.split('.') : raw.split(/\s+/);
  return parts.map((p) => p.trim()).filter(Boolean).map(wordVariants);
};

const accountTokens = (u) => tokenize(u.fullName || u.username || '');

// Every token in `needles` has a matching (overlapping) token somewhere in `haystack`.
const allTokensFound = (needles, haystack) => needles.every((n) => haystack.some((h) => keysOverlap(n, h)));

const findUserByName = (users, vorname, nachname) => {
  const target = [...tokenize(vorname), ...tokenize(nachname)];
  if (target.length === 0) return { match: null, candidates: [] };
  const candidates = users.filter((u) => {
    const acc = accountTokens(u);
    if (acc.length === 0) return false;
    // Account has (at least) every word the roster has — extra middle names are fine.
    if (allTokensFound(target, acc)) return true;
    // Account is a terse subset of the roster name (e.g. account "Bijoux" vs roster "Bijoux Lwembo").
    if (acc.length < target.length && allTokensFound(acc, target)) return true;
    return false;
  });
  return { match: candidates.length === 1 ? candidates[0] : null, candidates };
};

const findUsersByFirstName = (users, firstName) => {
  const target = wordVariants(firstName);
  if (target.size === 0) return [];
  return users.filter((u) => {
    const acc = accountTokens(u);
    return acc.length > 0 && keysOverlap(target, acc[0]);
  });
};

// Effective match for a row: an explicit override (including a deliberate
// "cleared" null) always wins over the auto-match so admins can correct
// wrong auto-matches, not just fill in missing ones.
const pick = (r) => (r.override !== undefined ? r.override : r.match);

// ── Day-group naming convention ──────────────────────────────────────────
//
// The backend only lets a user belong to one group at a time (a single
// `group_id` FK — "add member" just overwrites it, there's no join table).
// Two backend attempts at a proper dual-slot fix both crashed production
// tonight, so this stays on the plain, already-proven /api/groups/{id}/members
// endpoint deliberately: one group at a time, switched manually by re-running
// the other button when the day changes. Day-1 groups (numbers, ~30 people
// each) and Day-2 groups (letters, ~45 people each) are genuinely different
// sets of people, so they're never combined into one group/name.
const dayGroupName = (day, value) =>
  day === 1 ? `Welcome Days 2026 – Tag 1 – Gruppe ${value}` : `Welcome Days 2026 – Tag 2 – Gruppe ${value}`;

// Simple catch-all: since the real Day-1/Day-2 group is only ever accurate on the printed
// badge, this single group just flags "this person is a Welcome Days participant" — no
// day-switching needed. Matches the group already created in Group Management.
const SINGLE_GROUP_NAME = 'Deine Gruppe steht auf deinem Namenskleber';

// ── Excel parsing ─────────────────────────────────────────────────────────

const detectCol = (headers, patterns) =>
  headers.findIndex((h) => patterns.some((p) => (h || '').toString().toLowerCase().trim().includes(p)));

function parseGroupWorkbook(workbook) {
  // Prefer a sheet that looks like the day-group roster; fall back to the last sheet.
  let sheetName = workbook.SheetNames.find((n) => /tag ?2|gruppe/i.test(n));
  if (!sheetName) sheetName = workbook.SheetNames[workbook.SheetNames.length - 1];
  const ws = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (!rows.length) return { sheetName, entries: [] };

  const headers = rows[0].map((h) => (h || '').toString());
  const firstCol = detectCol(headers, ['vorname', 'first name', 'firstname']);
  const lastCol = detectCol(headers, ['nachname', 'last name', 'lastname']);
  const day1Col = detectCol(headers, ['gruppe 1', 'tag 1', 'group 1']);
  const day2Col = detectCol(headers, ['gruppe 2', 'tag 2', 'group 2']);

  const entries = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const vorname = firstCol >= 0 ? row[firstCol] : null;
    const nachname = lastCol >= 0 ? row[lastCol] : null;
    if (!vorname && !nachname) continue;
    const day1 = day1Col >= 0 ? row[day1Col] : null;
    const day2 = day2Col >= 0 ? row[day2Col] : null;
    if (day1 == null && day2 == null) continue;
    entries.push({
      vorname: (vorname || '').toString().trim(),
      nachname: (nachname || '').toString().trim(),
      day1: day1 != null ? day1.toString().trim() : null,
      day2: day2 != null ? day2.toString().trim() : null,
    });
  }
  return { sheetName, entries };
}

function parseRoomWorkbook(workbook) {
  const ws = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

  const entries = [];
  let currentGender = null;
  for (const row of rows) {
    if (!row || row.length === 0) continue;
    const first = row[0];
    if (typeof first === 'string' && /^(herren|frauen)$/i.test(first.trim())) {
      currentGender = first.trim();
      continue; // this row is the bed-number header, not data
    }
    if (typeof first === 'number') {
      const roomNumber = first;
      for (let col = 1; col < row.length; col++) {
        const name = row[col];
        if (name && name.toString().trim()) {
          entries.push({
            roomNumber,
            gender: currentGender,
            bed: col,
            firstName: name.toString().trim(),
          });
        }
      }
    }
  }
  return entries;
}

// ── Searchable user picker (replaces the old plain <select>) ─────────────
//
// Always visible/editable — even on rows that already auto-matched — so a
// wrong auto-match can be corrected, not just a missing one filled in.

const UserPicker = ({ users, value, suggested, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const q = query.trim().toLowerCase();
  const pool = q
    ? users.filter(
        (u) =>
          (u.username || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.fullName || '').toLowerCase().includes(q)
      )
    : suggested && suggested.length
    ? suggested
    : users;
  const list = pool.slice(0, 50);

  const isAmbiguous = !value && suggested && suggested.length > 1;

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 230 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          ...S.pickerBox,
          ...(value ? S.pickerBoxMatched : isAmbiguous ? S.pickerBoxAmbiguous : S.pickerBoxEmpty),
        }}
      >
        {value ? <Check size={13} color="#15803d" /> : <Search size={12} color={isAmbiguous ? '#b45309' : '#dc2626'} />}
        <span style={S.pickerBoxText}>
          {value ? value.username : isAmbiguous ? `${suggested.length} possible matches` : 'Not found — search…'}
        </span>
        <Pencil size={12} color="#94a3b8" />
      </div>
      {open && (
        <div style={S.pickerDropdown}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name or email…"
            style={S.pickerInput}
          />
          {value && (
            <div
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              style={S.pickerClearRow}
            >
              <X size={12} /> Clear match
            </div>
          )}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {list.length === 0 && <div style={S.pickerEmptyMsg}>No accounts found</div>}
            {list.map((u) => (
              <div
                key={u.id}
                onClick={() => {
                  onChange(u);
                  setOpen(false);
                  setQuery('');
                }}
                style={{ ...S.pickerOption, ...(value?.id === u.id ? S.pickerOptionActive : {}) }}
              >
                <span style={{ fontWeight: 600 }}>{u.username}</span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{u.email}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

const WelcomeDaysImport = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('groups'); // groups | rooms | badges

  const [groupSheetName, setGroupSheetName] = useState('');
  const [groupRows, setGroupRows] = useState([]); // {vorname,nachname,day1,day2,match,candidates,override}
  const [groupFilter, setGroupFilter] = useState('');
  const [groupUnresolvedOnly, setGroupUnresolvedOnly] = useState(false);
  const [assigningDay, setAssigningDay] = useState(null); // null | 1 | 2 — which day's groups are currently being written
  const [assignProgress, setAssignProgress] = useState(0);
  const [assignLog, setAssignLog] = useState([]);
  const [lastAssignedDay, setLastAssignedDay] = useState(null); // 1 | 2 — which one is currently live in the backend
  const [assigningSingle, setAssigningSingle] = useState(false);
  const [singleAssignProgress, setSingleAssignProgress] = useState(0);
  const [assigningAll, setAssigningAll] = useState(false);
  const [allAssignProgress, setAllAssignProgress] = useState(0);

  const [roomRows, setRoomRows] = useState([]); // {roomNumber,gender,bed,firstName,match,candidates,override}
  const [roomFilter, setRoomFilter] = useState('');
  const [roomUnresolvedOnly, setRoomUnresolvedOnly] = useState(false);
  const [roomAssigning, setRoomAssigning] = useState(false);
  const [roomAssignProgress, setRoomAssignProgress] = useState(0);
  const [roomAssignLog, setRoomAssignLog] = useState([]);

  const printRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const getToken = async () => await localforage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('No authentication token found. Please login again.');
      const headers = { Authorization: `Bearer ${token}` };

      // Note: /api/users/all is not a real route on this backend (it 500s,
      // falling through to the /api/users/{id} handler) — go straight to the
      // collection endpoint instead of wasting a request on it.
      const [usersRes, groupsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/users`, { headers }),
        axios.get(`${BASE_URL}/api/groups`, { headers }),
      ]);

      setUsers(usersRes.data.users || usersRes.data || []);
      setGroups(groupsRes.data.groups || groupsRes.data || []);
    } catch (err) {
      setError('Failed to load users/groups: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (a.username || '').localeCompare(b.username || '')),
    [users]
  );

  // ── Groups file upload ──────────────────────────────────────────────────

  const handleGroupFile = (file) => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array', cellDates: true });
        const { sheetName, entries } = parseGroupWorkbook(wb);
        if (!entries.length) {
          setError(`No usable rows found in sheet "${sheetName}". Expected columns like Vorname/Nachname/Gruppe 1/Gruppe 2.`);
          return;
        }
        setGroupSheetName(sheetName);
        const rows = entries.map((entry) => {
          const { match, candidates } = findUserByName(users, entry.vorname, entry.nachname);
          return { ...entry, match, candidates, override: undefined };
        });
        setGroupRows(rows);
        setAssignLog([]);
      } catch (err) {
        setError('Failed to parse Excel file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const groupStats = useMemo(() => {
    const matched = groupRows.filter((r) => pick(r)).length;
    const ambiguous = groupRows.filter((r) => !pick(r) && r.candidates?.length > 1).length;
    const unmatched = groupRows.filter((r) => !pick(r) && (!r.candidates || r.candidates.length <= 1)).length;
    return { total: groupRows.length, matched, ambiguous, unmatched };
  }, [groupRows]);

  const visibleGroupRows = useMemo(() => {
    const q = groupFilter.trim().toLowerCase();
    return groupRows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => {
        if (groupUnresolvedOnly && pick(r)) return false;
        if (!q) return true;
        return `${r.vorname} ${r.nachname}`.toLowerCase().includes(q);
      });
  }, [groupRows, groupFilter, groupUnresolvedOnly]);

  const setGroupOverride = (idx, user) => {
    setGroupRows((prev) => prev.map((r, i) => (i === idx ? { ...r, override: user } : r)));
  };

  const ensureGroup = async (name, token, groupCache) => {
    let g = groupCache.find((g) => g.name === name);
    if (g) return g;
    const res = await axios.post(
      `${BASE_URL}/api/groups`,
      {
        name,
        description: 'Welcome Days 2026',
        colorCode: '#da291c',
        membersLimit: 60,
        createdAt: new Date().toISOString(),
      },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    const newGroup = { id: res.data.id || res.data.groupId, name, members: [] };
    groupCache.push(newGroup);
    setGroups((prev) => [...prev, newGroup]);
    return newGroup;
  };

  // Assigns everyone to their Day-1 (day=1) or Day-2 (day=2) group — never
  // both, since the backend only allows one group per account. Re-running
  // this for the other day evicts everyone from whatever they're in now,
  // which is the intended way to switch the "live" grouping over.
  const runAssignGroups = async (day) => {
    const usable = groupRows.filter((r) => pick(r) && (day === 1 ? r.day1 : r.day2));
    if (usable.length === 0) {
      setError(`No matched participants have a Day-${day} value to assign.`);
      return;
    }
    const verb = lastAssignedDay && lastAssignedDay !== day ? 'move' : 'assign';
    if (
      !window.confirm(
        `This will ${verb} ${usable.length} participant(s) into their Day-${day} groups` +
          (lastAssignedDay === (day === 1 ? 2 : 1)
            ? ` — evicting them from their current Day-${lastAssignedDay} group, since an account can only hold one group.`
            : '.') +
          ' Continue?'
      )
    )
      return;

    setAssigningDay(day);
    setAssignProgress(0);
    setError(null);
    setSuccess('');
    const log = [];
    try {
      const token = await getToken();
      const groupCache = [...groups];

      for (let i = 0; i < usable.length; i++) {
        const row = usable[i];
        const user = pick(row);
        const value = day === 1 ? row.day1 : row.day2;
        try {
          const g = await ensureGroup(dayGroupName(day, value), token, groupCache);
          await axios
            .post(
              `${BASE_URL}/api/groups/${g.id}/members`,
              { email: user.email },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            )
            .catch((err) => {
              if (err.response?.status !== 409) throw err;
            });
          log.push({ name: `${row.vorname} ${row.nachname}`, ok: true });
        } catch (err) {
          log.push({
            name: `${row.vorname} ${row.nachname}`,
            ok: false,
            message: err.response?.data?.message || err.message,
          });
        }
        setAssignProgress(Math.round(((i + 1) / usable.length) * 100));
      }

      setAssignLog(log);
      const failCount = log.filter((l) => !l.ok).length;
      setSuccess(
        `Done. ${log.length - failCount}/${log.length} participants are now in their Day-${day} group.` +
          (failCount ? ` ${failCount} failed — see log below.` : '')
      );
      setLastAssignedDay(day);
      await fetchData();
    } catch (err) {
      setError('Import failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssigningDay(null);
    }
  };

  // Simple alternative to the Day-1/Day-2 buttons above: puts every matched participant into
  // one flag group (SINGLE_GROUP_NAME) regardless of their day1/day2 value — the real number
  // and letter only ever need to be on the printed badge, not tracked as separate backend
  // groups. Bypasses Group Management's bulk-select entirely; same proven /members endpoint.
  const runAssignSingleGroup = async () => {
    const usable = groupRows.filter((r) => pick(r));
    if (usable.length === 0) {
      setError('No matched participants to assign.');
      return;
    }
    if (!window.confirm(`This will add ${usable.length} participant(s) to "${SINGLE_GROUP_NAME}". Continue?`)) return;

    setAssigningSingle(true);
    setSingleAssignProgress(0);
    setError(null);
    setSuccess('');
    const log = [];
    try {
      const token = await getToken();
      const groupCache = [...groups];
      const group = await ensureGroup(SINGLE_GROUP_NAME, token, groupCache);

      for (let i = 0; i < usable.length; i++) {
        const row = usable[i];
        const user = pick(row);
        try {
          await axios
            .post(
              `${BASE_URL}/api/groups/${group.id}/members`,
              { email: user.email },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            )
            .catch((err) => {
              if (err.response?.status !== 409) throw err;
            });
          log.push({ name: `${row.vorname} ${row.nachname}`, ok: true });
        } catch (err) {
          log.push({
            name: `${row.vorname} ${row.nachname}`,
            ok: false,
            message: err.response?.data?.message || err.message,
          });
        }
        setSingleAssignProgress(Math.round(((i + 1) / usable.length) * 100));
      }

      setAssignLog(log);
      const failCount = log.filter((l) => !l.ok).length;
      setSuccess(
        `Done. ${log.length - failCount}/${log.length} participants added to "${SINGLE_GROUP_NAME}".` +
          (failCount ? ` ${failCount} failed — see log below.` : '')
      );
      await fetchData();
    } catch (err) {
      setError('Import failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssigningSingle(false);
    }
  };

  // Adds literally every loaded account (not just matched Welcome Days participants) to the
  // flag group — for when who's in it doesn't matter, only that everyone has the label. Also
  // sidesteps Group Management's "member limit" check entirely, which is based on a hardcoded
  // value the backend always reports (50) and doesn't actually persist anywhere.
  const runAssignAllUsers = async () => {
    if (users.length === 0) {
      setError('No accounts loaded to assign.');
      return;
    }
    if (!window.confirm(`This will add ALL ${users.length} loaded accounts to "${SINGLE_GROUP_NAME}", not just matched participants. Continue?`)) return;

    setAssigningAll(true);
    setAllAssignProgress(0);
    setError(null);
    setSuccess('');
    const log = [];
    try {
      const token = await getToken();
      const groupCache = [...groups];
      const group = await ensureGroup(SINGLE_GROUP_NAME, token, groupCache);

      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        try {
          await axios
            .post(
              `${BASE_URL}/api/groups/${group.id}/members`,
              { email: user.email },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            )
            .catch((err) => {
              if (err.response?.status !== 409) throw err;
            });
          log.push({ name: user.username, ok: true });
        } catch (err) {
          log.push({ name: user.username, ok: false, message: err.response?.data?.message || err.message });
        }
        setAllAssignProgress(Math.round(((i + 1) / users.length) * 100));
      }

      setAssignLog(log);
      const failCount = log.filter((l) => !l.ok).length;
      setSuccess(
        `Done. ${log.length - failCount}/${log.length} accounts added to "${SINGLE_GROUP_NAME}".` +
          (failCount ? ` ${failCount} failed — see log below.` : '')
      );
      await fetchData();
    } catch (err) {
      setError('Import failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssigningAll(false);
    }
  };

  // ── Rooms file upload ────────────────────────────────────────────────────

  const handleRoomFile = (file) => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array', cellDates: true });
        const entries = parseRoomWorkbook(wb);
        if (!entries.length) {
          setError('No room/bed entries found. Expected a sheet with room numbers followed by names in bed columns.');
          return;
        }
        const rows = entries.map((entry) => {
          const candidates = findUsersByFirstName(users, entry.firstName);
          const match = candidates.length === 1 ? candidates[0] : null;
          return { ...entry, match, candidates, override: undefined };
        });
        setRoomRows(rows);
        setRoomAssignLog([]);
      } catch (err) {
        setError('Failed to parse Excel file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const roomStats = useMemo(() => {
    const matched = roomRows.filter((r) => pick(r)).length;
    const ambiguous = roomRows.filter((r) => !pick(r) && r.candidates?.length > 1).length;
    const unmatched = roomRows.filter((r) => !pick(r) && (!r.candidates || r.candidates.length <= 1)).length;
    return { total: roomRows.length, matched, ambiguous, unmatched };
  }, [roomRows]);

  const visibleRoomRows = useMemo(() => {
    const q = roomFilter.trim().toLowerCase();
    return roomRows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => {
        if (roomUnresolvedOnly && pick(r)) return false;
        if (!q) return true;
        return r.firstName.toLowerCase().includes(q) || r.roomNumber.toString().includes(q);
      });
  }, [roomRows, roomFilter, roomUnresolvedOnly]);

  const setRoomOverride = (idx, user) => {
    setRoomRows((prev) => prev.map((r, i) => (i === idx ? { ...r, override: user } : r)));
  };

  const runAssignRooms = async () => {
    const usable = roomRows.filter((r) => pick(r));
    if (usable.length === 0) {
      setError('No matched participants to assign.');
      return;
    }
    if (!window.confirm(`This will assign ${usable.length} participant(s) to their rooms. Continue?`)) return;

    setRoomAssigning(true);
    setRoomAssignProgress(0);
    setError(null);
    setSuccess('');
    const log = [];
    try {
      const token = await getToken();
      for (let i = 0; i < usable.length; i++) {
        const row = usable[i];
        const user = pick(row);
        try {
          await axios.post(
            `${BASE_URL}/api/rooms/assign`,
            { email: user.email, roomNumber: row.roomNumber.toString() },
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          log.push({ name: row.firstName, room: row.roomNumber, ok: true });
        } catch (err) {
          log.push({
            name: row.firstName,
            room: row.roomNumber,
            ok: false,
            message: err.response?.data?.message || err.message,
          });
        }
        setRoomAssignProgress(Math.round(((i + 1) / usable.length) * 100));
      }
      setRoomAssignLog(log);
      const failCount = log.filter((l) => !l.ok).length;
      setSuccess(
        `Done. ${log.length - failCount}/${log.length} participants assigned to rooms.` +
          (failCount ? ` ${failCount} failed — see log below.` : '')
      );
    } catch (err) {
      setError('Room import failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setRoomAssigning(false);
    }
  };

  // ── Badge printing ───────────────────────────────────────────────────────

  const badgeEntries = useMemo(
    () =>
      groupRows
        .filter((r) => pick(r) && (r.day1 || r.day2))
        .map((r) => ({
          name: `${r.vorname} ${r.nachname}`.trim() || pick(r).username,
          day1: r.day1,
          day2: r.day2,
        })),
    [groupRows]
  );

  const handlePrint = () => window.print();

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={S.loadingContainer}>
        <div style={S.spinner} />
        <p style={S.loadingText}>Loading Welcome Days Import…</p>
      </div>
    );
  }

  return (
    <div style={S.container} className="wdi-no-print">
      <style>{printCss}</style>

      <div style={S.header}>
        <div>
          <h1 style={S.title}>Welcome Days 2026 – Import</h1>
          <p style={S.subtitle}>Day-1/Day-2 groups, rooms and printable name badges from the Excel files</p>
        </div>
        <button className="wdi-refresh-btn" onClick={fetchData} style={S.refreshBtn} disabled={assigningDay !== null || roomAssigning}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div style={S.statsBar}>
        <div style={S.statCard}>
          <div style={{ ...S.statIcon, backgroundColor: '#eff6ff' }}>
            <Users size={20} color="#3b82f6" />
          </div>
          <div>
            <div style={S.statNumber}>{users.length}</div>
            <div style={S.statLabel}>Accounts loaded</div>
          </div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statIcon, backgroundColor: '#fffbeb' }}>
            <Users size={20} color="#f59e0b" />
          </div>
          <div>
            <div style={S.statNumber}>{groups.length}</div>
            <div style={S.statLabel}>Groups</div>
          </div>
        </div>
      </div>

      {success && (
        <div style={S.successAlert}>
          <CheckCircle size={17} />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div style={S.errorAlert}>
          <AlertCircle size={17} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={S.dismissBtn}>
            <X size={14} />
          </button>
        </div>
      )}

      <div style={S.tabNav}>
        {[
          { key: 'groups', icon: <Users size={15} />, label: 'Day Groups' },
          { key: 'rooms', icon: <Home size={15} />, label: 'Rooms' },
          { key: 'badges', icon: <IdCard size={15} />, label: 'Print Badges' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{ ...S.tabBtn, ...(activeTab === t.key ? S.tabBtnActive : {}) }}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ GROUPS TAB ══ */}
      {activeTab === 'groups' && (
        <div>
          <div style={S.infoBar}>
            <Info size={15} color="#0ea5e9" />
            <span>
              Upload "Teilnehmerliste Welcome Days 2026.xlsx" (sheet "Gruppeneinteilung Tag 2"). Accounts here can
              only hold <em>one</em> group at a time, so Day-1 groups (numbers, ~30 people each) and Day-2 groups
              (letters, ~45 people each) are two separate actions below — switch by re-running the other one when
              the day changes. The printed badge always shows both values regardless of which is currently live.
              Click any matched account to change it if it picked the wrong person.
            </span>
          </div>

          <div style={S.uploadCard}>
            <Upload size={22} color="#f97316" />
            <div style={{ flex: 1 }}>
              <h3 style={S.uploadTitle}>Upload participant/group list</h3>
              <p style={S.uploadSub}>{groupSheetName ? `Parsed sheet: "${groupSheetName}" — ${groupRows.length} rows` : 'xlsx with Vorname, Nachname, Gruppe 1 Tag, Gruppe 2 Tag'}</p>
            </div>
            <label htmlFor="group-file" style={S.uploadBtn}>
              Choose file
            </label>
            <input
              id="group-file"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files[0];
                if (f) handleGroupFile(f);
                e.target.value = '';
              }}
            />
          </div>

          {groupRows.length > 0 && (
            <>
              <div style={S.statsBar}>
                <MiniStat icon={<UserCheck size={18} color="#22c55e" />} bg="#f0fdf4" value={groupStats.matched} label="Matched" />
                <MiniStat icon={<HelpCircle size={18} color="#f59e0b" />} bg="#fffbeb" value={groupStats.ambiguous} label="Ambiguous" />
                <MiniStat icon={<UserX size={18} color="#ef4444" />} bg="#fef2f2" value={groupStats.unmatched} label="Not found" />
              </div>

              <FilterBar
                filter={groupFilter}
                setFilter={setGroupFilter}
                unresolvedOnly={groupUnresolvedOnly}
                setUnresolvedOnly={setGroupUnresolvedOnly}
                placeholder="Search participant name…"
                shownCount={visibleGroupRows.length}
                totalCount={groupRows.length}
              />

              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Participant</th>
                      <th style={S.th}>Tag 1 (Nr.)</th>
                      <th style={S.th}>Tag 2 (Buchst.)</th>
                      <th style={S.th}>Matched account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGroupRows.map(({ r, idx }) => (
                      <tr key={idx}>
                        <td style={S.td}>
                          {r.vorname} {r.nachname}
                        </td>
                        <td style={S.td}>{r.day1 ? <span style={S.numBadge}>{r.day1}</span> : '—'}</td>
                        <td style={S.td}>{r.day2 ? <span style={S.letterBadge}>{r.day2}</span> : '—'}</td>
                        <td style={S.td}>
                          <UserPicker
                            users={sortedUsers}
                            value={pick(r)}
                            suggested={r.candidates}
                            onChange={(u) => setGroupOverride(idx, u)}
                          />
                        </td>
                      </tr>
                    ))}
                    {visibleGroupRows.length === 0 && (
                      <tr>
                        <td style={S.td} colSpan={4}>
                          No rows match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={S.infoBar}>
                <Info size={15} color="#0ea5e9" />
                <span>
                  Recommended: skip the Day-1/Day-2 group juggling entirely and just flag everyone as a Welcome Days
                  participant in one group — the real number/letter only ever needs to be on the printed badge
                  anyway. Bypasses the broken bulk-select in Group Management too.
                </span>
              </div>
              <div style={{ ...S.actionsRow, gap: 10 }}>
                <button style={S.primaryBtn} onClick={runAssignSingleGroup} disabled={assigningSingle || assigningAll}>
                  {assigningSingle
                    ? `Assigning… ${singleAssignProgress}%`
                    : `Assign ${groupStats.matched} matched participants to "${SINGLE_GROUP_NAME}"`}
                </button>
                <button
                  style={{ ...S.primaryBtn, backgroundColor: '#475569', boxShadow: '0 3px 10px rgba(71,85,105,0.28)' }}
                  onClick={runAssignAllUsers}
                  disabled={assigningSingle || assigningAll}
                  title="Adds every loaded account, not just matched Welcome Days participants"
                >
                  {assigningAll ? `Assigning… ${allAssignProgress}%` : `Or: add ALL ${users.length} accounts`}
                </button>
              </div>

              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Advanced: separate Day-1 / Day-2 groups instead
                </summary>
                <div style={{ marginTop: 12 }}>
                  {lastAssignedDay && (
                    <div style={S.liveBar}>
                      <CheckCircle size={14} color="#15803d" />
                      Day-{lastAssignedDay} groups are currently live in the backend for these participants.
                    </div>
                  )}
                  <div style={{ ...S.actionsRow, justifyContent: 'flex-end', gap: 10 }}>
                    <button
                      style={{ ...S.primaryBtn, ...S.day1Btn }}
                      onClick={() => runAssignGroups(1)}
                      disabled={assigningDay !== null}
                    >
                      {assigningDay === 1 ? `Assigning… ${assignProgress}%` : `Assign Day-1 Groups (numbers)`}
                    </button>
                    <button
                      style={{ ...S.primaryBtn, ...S.day2Btn }}
                      onClick={() => runAssignGroups(2)}
                      disabled={assigningDay !== null}
                    >
                      {assigningDay === 2 ? `Assigning… ${assignProgress}%` : `Assign Day-2 Groups (letters)`}
                    </button>
                  </div>
                </div>
              </details>

              {assignLog.length > 0 && <AssignLog log={assignLog} />}
            </>
          )}
        </div>
      )}

      {/* ══════════════════ ROOMS TAB ══ */}
      {activeTab === 'rooms' && (
        <div>
          <div style={S.infoBar}>
            <Info size={15} color="#0ea5e9" />
            <span>
              Upload "Zimmereinteilung Welcome Days 2026.xlsx". The sheet only has first names, so ambiguous matches
              (same first name, multiple accounts) need a manual pick below.
            </span>
          </div>

          <div style={S.uploadCard}>
            <Upload size={22} color="#f97316" />
            <div style={{ flex: 1 }}>
              <h3 style={S.uploadTitle}>Upload room list</h3>
              <p style={S.uploadSub}>{roomRows.length > 0 ? `${roomRows.length} bed entries parsed` : 'xlsx with room numbers and names in bed columns'}</p>
            </div>
            <label htmlFor="room-file" style={S.uploadBtn}>
              Choose file
            </label>
            <input
              id="room-file"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files[0];
                if (f) handleRoomFile(f);
                e.target.value = '';
              }}
            />
          </div>

          {roomRows.length > 0 && (
            <>
              <div style={S.statsBar}>
                <MiniStat icon={<UserCheck size={18} color="#22c55e" />} bg="#f0fdf4" value={roomStats.matched} label="Matched" />
                <MiniStat icon={<HelpCircle size={18} color="#f59e0b" />} bg="#fffbeb" value={roomStats.ambiguous} label="Ambiguous" />
                <MiniStat icon={<UserX size={18} color="#ef4444" />} bg="#fef2f2" value={roomStats.unmatched} label="Not found" />
              </div>

              <FilterBar
                filter={roomFilter}
                setFilter={setRoomFilter}
                unresolvedOnly={roomUnresolvedOnly}
                setUnresolvedOnly={setRoomUnresolvedOnly}
                placeholder="Search first name or room number…"
                shownCount={visibleRoomRows.length}
                totalCount={roomRows.length}
              />

              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Room</th>
                      <th style={S.th}>Bed</th>
                      <th style={S.th}>First name (file)</th>
                      <th style={S.th}>Matched account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRoomRows.map(({ r, idx }) => (
                      <tr key={idx}>
                        <td style={S.td}>
                          {r.roomNumber} <span style={{ color: '#94a3b8', fontSize: 11 }}>({r.gender})</span>
                        </td>
                        <td style={S.td}>{r.bed}</td>
                        <td style={S.td}>{r.firstName}</td>
                        <td style={S.td}>
                          <UserPicker
                            users={sortedUsers}
                            value={pick(r)}
                            suggested={r.candidates}
                            onChange={(u) => setRoomOverride(idx, u)}
                          />
                        </td>
                      </tr>
                    ))}
                    {visibleRoomRows.length === 0 && (
                      <tr>
                        <td style={S.td} colSpan={4}>
                          No rows match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={S.actionsRow}>
                <button style={S.primaryBtn} onClick={runAssignRooms} disabled={roomAssigning}>
                  {roomAssigning ? `Assigning… ${roomAssignProgress}%` : `Assign ${roomStats.matched} participants to rooms`}
                </button>
              </div>

              {roomAssignLog.length > 0 && <AssignLog log={roomAssignLog} />}
            </>
          )}
        </div>
      )}

      {/* ══════════════════ BADGES TAB ══ */}
      {activeTab === 'badges' && (
        <div>
          <div style={S.infoBar}>
            <Info size={15} color="#0ea5e9" />
            <span>
              Printable name badges — one per participant from the Day Groups list, showing their Day-1 number and
              Day-2 letter. Upload the group list on the "Day Groups" tab first.
            </span>
          </div>

          {badgeEntries.length === 0 ? (
            <div style={S.emptyState}>
              <IdCard size={48} color="#cbd5e1" />
              <h3 style={{ margin: '8px 0 4px', color: '#64748b' }}>No badges yet</h3>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
                Upload the participant/group list on the "Day Groups" tab to generate badges.
              </p>
            </div>
          ) : (
            <>
              <div style={S.actionsRow}>
                <button style={S.primaryBtn} onClick={handlePrint}>
                  <Printer size={16} style={{ marginRight: 8 }} />
                  Print {badgeEntries.length} badges
                </button>
              </div>

              <div className="wdi-print-area" ref={printRef} style={S.badgeGrid}>
                {badgeEntries.map((b, i) => (
                  <div key={i} className="wdi-badge" style={S.badge}>
                    <div style={S.badgeEvent}>Welcome Days 2026</div>
                    <div style={S.badgeName}>{b.name}</div>
                    <div style={S.badgeGroups}>
                      <div style={S.badgeGroupCol}>
                        <span style={S.badgeGroupLabel}>Tag 1</span>
                        <span style={S.numBadgeBig}>{b.day1 || '–'}</span>
                      </div>
                      <div style={S.badgeGroupCol}>
                        <span style={S.badgeGroupLabel}>Tag 2</span>
                        <span style={S.letterBadgeBig}>{b.day2 || '–'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ icon, bg, value, label }) => (
  <div style={S.statCard}>
    <div style={{ ...S.statIcon, backgroundColor: bg }}>{icon}</div>
    <div>
      <div style={S.statNumber}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  </div>
);

const FilterBar = ({ filter, setFilter, unresolvedOnly, setUnresolvedOnly, placeholder, shownCount, totalCount }) => (
  <div style={S.filterBar}>
    <div style={S.filterSearchWrap}>
      <Search size={14} color="#94a3b8" />
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={placeholder}
        style={S.filterInput}
      />
      {filter && (
        <button onClick={() => setFilter('')} style={S.filterClearBtn}>
          <X size={12} />
        </button>
      )}
    </div>
    <label style={S.filterCheckboxLabel}>
      <input type="checkbox" checked={unresolvedOnly} onChange={(e) => setUnresolvedOnly(e.target.checked)} />
      Show unresolved only
    </label>
    <span style={S.filterCount}>
      {shownCount}/{totalCount} shown
    </span>
  </div>
);

const AssignLog = ({ log }) => {
  const failures = log.filter((l) => !l.ok);
  if (failures.length === 0) return null;
  return (
    <div style={S.logBox}>
      <strong style={{ color: '#dc2626', fontSize: 13 }}>Failed ({failures.length}):</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b' }}>
        {failures.map((f, i) => (
          <li key={i}>
            {f.name}
            {f.room ? ` (Room ${f.room})` : ''}: {f.message}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Print CSS ────────────────────────────────────────────────────────────

const printCss = `
  @media print {
    body * { visibility: hidden; }
    .wdi-print-area, .wdi-print-area * { visibility: visible; }
    .wdi-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .wdi-badge { break-inside: avoid; }
  }
`;

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  container: {
    padding: 24,
    maxWidth: 1400,
    margin: '0 auto',
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loadingContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100vh', gap: 14, backgroundColor: '#f8fafc',
  },
  spinner: {
    width: 42, height: 42, border: '3px solid #f1f5f9', borderTop: '3px solid #da291c',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  loadingText: { color: '#64748b', fontSize: 14 },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18,
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', padding: '22px 26px',
    borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  },
  title: { fontSize: 24, fontWeight: 800, color: 'white', margin: 0, letterSpacing: '-0.4px' },
  subtitle: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
    backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 },
  statCard: {
    display: 'flex', alignItems: 'center', gap: 14, backgroundColor: 'white', padding: '16px 18px',
    borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.055)', border: '1px solid #f1f5f9',
  },
  statIcon: { width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statNumber: { fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1 },
  statLabel: { fontSize: 11, color: '#64748b', marginTop: 3, fontWeight: 500 },

  successAlert: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '11px 15px', backgroundColor: '#f0fdf4',
    color: '#15803d', borderRadius: 10, marginBottom: 14, border: '1px solid #bbf7d0', fontSize: 13, fontWeight: 500,
  },
  errorAlert: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '11px 15px', backgroundColor: '#fef2f2',
    color: '#dc2626', borderRadius: 10, marginBottom: 14, border: '1px solid #fecaca', fontSize: 13, fontWeight: 500,
  },
  dismissBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', alignItems: 'center', padding: 2 },

  tabNav: {
    display: 'flex', gap: 4, backgroundColor: 'white', padding: 5, borderRadius: 12, marginBottom: 20,
    boxShadow: '0 1px 6px rgba(0,0,0,0.055)', border: '1px solid #f1f5f9', width: 'fit-content',
  },
  tabBtn: {
    display: 'flex', alignItems: 'center', gap: 7, padding: '10px 22px', backgroundColor: 'transparent', border: 'none',
    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#64748b',
  },
  tabBtnActive: { backgroundColor: '#da291c', color: 'white', boxShadow: '0 3px 10px rgba(218,41,28,0.28)' },

  infoBar: {
    display: 'flex', alignItems: 'flex-start', gap: 9, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd',
    borderRadius: 10, padding: '11px 15px', marginBottom: 18, fontSize: 13, color: '#0369a1', lineHeight: 1.5,
  },

  uploadCard: {
    display: 'flex', alignItems: 'center', gap: 16, backgroundColor: 'white', border: '1.5px dashed #fed7aa',
    borderRadius: 14, padding: '18px 22px', marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  },
  uploadTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 2px' },
  uploadSub: { fontSize: 12, color: '#64748b', margin: 0 },
  uploadBtn: {
    display: 'inline-flex', alignItems: 'center', padding: '10px 18px', backgroundColor: '#f97316', color: 'white',
    border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
  },

  filterBar: {
    display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, flexWrap: 'wrap',
  },
  filterSearchWrap: {
    display: 'flex', alignItems: 'center', gap: 7, backgroundColor: 'white', border: '1.5px solid #e2e8f0',
    borderRadius: 10, padding: '8px 12px', flex: '1 1 260px', maxWidth: 360,
  },
  filterInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, color: '#1e293b', background: 'transparent' },
  filterClearBtn: { display: 'flex', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: 2 },
  filterCheckboxLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' },
  filterCount: { fontSize: 12, color: '#94a3b8', marginLeft: 'auto' },

  tableWrap: { backgroundColor: 'white', borderRadius: 14, overflow: 'auto', border: '1px solid #f1f5f9', marginBottom: 18, maxHeight: 520 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8fafc', color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', position: 'sticky', top: 0 },
  td: { padding: '9px 14px', borderTop: '1px solid #f1f5f9', color: '#374151' },

  numBadge: { display: 'inline-flex', width: 24, height: 24, alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef2ff', color: '#4338ca', borderRadius: '50%', fontWeight: 700, fontSize: 12 },
  letterBadge: { display: 'inline-flex', width: 24, height: 24, alignItems: 'center', justifyContent: 'center', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '50%', fontWeight: 700, fontSize: 12 },

  // User picker
  pickerBox: {
    display: 'flex', alignItems: 'center', gap: 7, padding: '6px 9px', borderRadius: 8, cursor: 'pointer',
    border: '1.5px solid transparent', fontSize: 12, fontWeight: 600,
  },
  pickerBoxText: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pickerBoxMatched: { backgroundColor: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' },
  pickerBoxAmbiguous: { backgroundColor: '#fffbeb', color: '#b45309', borderColor: '#fde68a' },
  pickerBoxEmpty: { backgroundColor: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' },
  pickerDropdown: {
    position: 'absolute', top: '100%', left: 0, marginTop: 4, width: 300, backgroundColor: 'white',
    border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', zIndex: 20, padding: 6,
  },
  pickerInput: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0',
    fontSize: 13, outline: 'none', marginBottom: 4,
  },
  pickerClearRow: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', fontSize: 12, color: '#dc2626',
    cursor: 'pointer', borderRadius: 6,
  },
  pickerEmptyMsg: { padding: '10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' },
  pickerOption: {
    display: 'flex', flexDirection: 'column', padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
  pickerOptionActive: { backgroundColor: '#f0fdf4' },

  actionsRow: { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 },
  primaryBtn: {
    display: 'flex', alignItems: 'center', padding: '11px 20px', backgroundColor: '#da291c', color: 'white',
    border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 10px rgba(218,41,28,0.28)',
  },
  day1Btn: { backgroundColor: '#4338ca', boxShadow: '0 3px 10px rgba(67,56,202,0.28)' },
  day2Btn: { backgroundColor: '#d97706', boxShadow: '0 3px 10px rgba(217,119,6,0.28)' },

  liveBar: {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#15803d',
    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 12,
  },

  logBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 18 },

  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 20px', textAlign: 'center' },

  badgeGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  badge: {
    border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', backgroundColor: 'white',
    display: 'flex', flexDirection: 'column', gap: 8, minHeight: 130,
  },
  badgeEvent: { fontSize: 10, fontWeight: 700, color: '#da291c', textTransform: 'uppercase', letterSpacing: '0.5px' },
  badgeName: { fontSize: 16, fontWeight: 800, color: '#1e293b' },
  badgeGroups: { display: 'flex', gap: 16, marginTop: 'auto' },
  badgeGroupCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  badgeGroupLabel: { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' },
  numBadgeBig: { display: 'inline-flex', width: 40, height: 40, alignItems: 'center', justifyContent: 'center', backgroundColor: '#4338ca', color: 'white', borderRadius: '50%', fontWeight: 800, fontSize: 18 },
  letterBadgeBig: { display: 'inline-flex', width: 40, height: 40, alignItems: 'center', justifyContent: 'center', backgroundColor: '#d97706', color: 'white', borderRadius: '50%', fontWeight: 800, fontSize: 18 },
};

export default WelcomeDaysImport;
